import { Card, CardContent, CardHeader } from "@/components/layouts/Card";
import PageLoader from "@/components/loader/PageLoader";
import OptionListSelectionModal from "@/components/modals/OptionListSelectionModal";
import ReorderableList from "@/components/modals/ReorderableList";
import { useUser } from "@/contexts/UserContext";
import { Category, OperatingHour, OptionList } from "@/utils/types";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { Input } from "@headlessui/react";
import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const AddCategory = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();

  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [category, setCategory] = useState<Category>({
    id: 0,
    name: "",
    description: "",
    status: "Active",
    parent_category: null,
    category_option_lists: [],
    category_hours: [],
  });
  const [isOptionListModalOpen, setIsOptionListModalOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [operatingHours, setOperatingHours] = useState<OperatingHour[]>([]);

  useEffect(() => {
    const fetchAllCategories = async () => {
      if (!loading && axiosInstance && user?.selectedBranch) {
        try {
          const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}${
            import.meta.env.VITE_APP_REDBIRDPOSBE_CATEGORY_INFORMATION
          }`;

          const url = `${apiUrl}?branch=${user.selectedBranch.id}&page=1&page_size=100`;
          const response = await axiosInstance.get(url);
          const { results } = response.data;
          setCategories(results);
        } catch (error) {
          console.error("Error fetching categories:", error);
        }
      }
    };

    fetchAllCategories();
  }, [axiosInstance, loading, user?.selectedBranch]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading || !axiosInstance) return;
    const selectedBranch = user?.selectedBranch?.id;
    const highestRanking = Math.max(
      ...categories.map((cat) => cat.ranking || 0),
      0,
    );
    const newRanking = highestRanking + 1;
    const transformedOptionLists = category.category_option_lists.map(
      (optionList) => ({
        id: optionList.id,
        name: optionList.name,
        branch: optionList.branch,
        options: optionList.options.map((option) => option.id),
        required: optionList.required,
        option_type: optionList.option_type,
        max_selections: optionList.max_selections,
      }),
    );

    const categoryData = {
      ...category,
      branch: selectedBranch,
      ranking: newRanking,
      category_option_lists: transformedOptionLists,
    };

    try {
      const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}${
        import.meta.env.VITE_APP_REDBIRDPOSBE_CATEGORY_INFORMATION
      }`;

      const response = await axiosInstance.post(apiUrl, categoryData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      const hoursApiUrl =
        import.meta.env.VITE_APP_REDBIRDPOSBE_CATEGORY_HOURS ?? "";
      for (const hour of operatingHours) {
        await axiosInstance.post(hoursApiUrl, {
          day: hour.day,
          open_time: hour.open_time,
          close_time: hour.close_time,
          category: response.data.id,
        });
      }
      toast.success(t("back.management.menu.category.successadd"));
      navigate("/menu/categories");
    } catch (error) {
      console.error("Error adding category:", error);
      toast.error(t("back.management.menu.category.failadd"));
    }
  };

  const handleOptionListSave = (selectedOptionLists: OptionList[]) => {
    const updatedOptionLists = selectedOptionLists.map((optionList) => {
      const existingOptionList = category.category_option_lists.find(
        (existingList) => existingList.id === optionList.id,
      );

      return existingOptionList
        ? { ...existingOptionList, ...optionList }
        : optionList;
    });

    setCategory((prevCategory) => ({
      ...prevCategory,
      category_option_lists: updatedOptionLists,
    }));

    setIsOptionListModalOpen(false);
  };

  const handleRemoveOptionList = (optionList: OptionList) => {
    setCategory((prevCategory) => ({
      ...prevCategory,
      category_option_lists: prevCategory.category_option_lists.filter(
        (list) => list.id !== optionList.id,
      ),
    }));
  };

  const statusOptions = [
    <option key="active" value="Active">
      {t("back.management.menu.category.status1")}
    </option>,
    <option key="inactivate" value="Inactive">
      {t("back.management.menu.category.status2")}
    </option>,
    <option key="noProduct" value="No Product">
      {t("back.management.menu.category.status3")}
    </option>,
    <option key="draft" value="Draft">
      {t("back.management.menu.category.status4")}
    </option>,
  ];

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto px-4 py-2">
        <h1 className="text-xl font-bold">
          {t("back.management.menu.category.addnewcategory")}
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">
              {t("back.management.menu.category.generalinfo")}
            </h2>
            <div className="-mx-2 flex flex-wrap">
              <div className="mb-4 w-full px-2 md:mb-0 md:w-full">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="categoryName"
                >
                  {t("back.management.menu.category.categoryname")}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  placeholder={t(
                    "back.management.menu.category.entercategoryname",
                  )}
                  value={category.name}
                  onChange={(e) =>
                    setCategory({ ...category, name: e.target.value })
                  }
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                />
              </div>
            </div>
            <div className="mt-4">
              <label
                className="mb-2 block text-sm font-bold text-gray-700"
                htmlFor="description"
              >
                {t("back.management.menu.category.description")}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                required
                placeholder={t(
                  "back.management.menu.category.enterdescription",
                )}
                value={category.description}
                onChange={(e) =>
                  setCategory({ ...category, description: e.target.value })
                }
                className="block h-48 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
              />
            </div>
            <div className="mt-4 flex items-center space-x-4">
              <label
                className="mb-2 block text-sm font-bold text-gray-700"
                htmlFor="status"
              >
                {t("back.management.menu.category.status")}
              </label>
              <Input
                className="h-6 w-6 text-center"
                type="checkbox"
                id="status"
                checked={category.status === "Active"}
                onChange={(e) =>
                  setCategory({
                    ...category,
                    status: e.target.checked ? "Active" : "Inactive",
                  })
                }
              />
              <span>
                {category.status === "Active" ? "Active" : "Inactive"}
              </span>
            </div>
          </div>

          <Card className="mb-4">
            <CardHeader className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {t("Linked OptionLists")}
              </h2>
              <button
                type="button"
                onClick={() => setIsOptionListModalOpen(true)}
                className="flex items-center rounded-md bg-gray-500 px-3 py-1 text-white"
              >
                {t("Add")}
              </button>
            </CardHeader>
            <CardContent>
              <ReorderableList
                items={category.category_option_lists}
                onReorder={(newOptionList) =>
                  setCategory({
                    ...category,
                    category_option_lists: newOptionList,
                  })
                }
                onRemove={handleRemoveOptionList}
                renderItem={(optionList) => optionList.name}
              />
            </CardContent>
          </Card>

          <div className="mt-4 flex justify-end">
            <Link
              to="/menu/categories"
              className="mr-2 rounded-md bg-gray-500 px-4 py-2 text-white"
            >
              {t("back.management.menu.category.cancelbutton")}
            </Link>
            <button
              type="submit"
              className="rounded-md bg-red-500 px-4 py-2 text-white"
            >
              {t("back.management.menu.category.confirmbutton")}
            </button>
          </div>
        </form>
        <OptionListSelectionModal
          isOpen={isOptionListModalOpen}
          onClose={() => setIsOptionListModalOpen(false)}
          onSave={handleOptionListSave}
          initialSelectedOptionLists={category.category_option_lists}
        />
      </div>
    </PageLoader>
  );
};

export default AddCategory;
